/**
 * Catalogue hooks.
 *
 * Note what is absent: there is no `useEffect` that copies search results into state. The index
 * is an external store read during render, and `search()` is a pure function of the index and
 * the query — so `useMemo` is the whole of it, and results are never one frame stale.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { getCatalogueSnapshot, subscribeToCatalogue, type CatalogueState } from './store.js';
import { search, type SearchHit, type SearchOptions } from './search.js';
import type { CatalogueVariant, VariantId } from '@pos/shared';

export function useCatalogue(): CatalogueState {
  return useSyncExternalStore(subscribeToCatalogue, getCatalogueSnapshot, getCatalogueSnapshot);
}

export function useCatalogueSearch(query: string, options: SearchOptions = {}): readonly SearchHit[] {
  const { index } = useCatalogue();
  const { limit, inStockOnly } = options;

  return useMemo(() => {
    // Built rather than spread: under `exactOptionalPropertyTypes`, passing an explicit
    // `undefined` is not the same as omitting the key, and `search` reads the absence.
    const opts: SearchOptions = {
      ...(limit !== undefined && { limit }),
      ...(inStockOnly !== undefined && { inStockOnly }),
    };

    return search(index, query, opts);
    // `index` is a new object only when the catalogue actually changed, so this recomputes on a
    // keystroke and on a sync, and on nothing else.
  }, [index, query, limit, inStockOnly]);
}

/** One variant by id, straight from the index. Used by the cart to re-read stock after a sync. */
export function useVariant(id: VariantId | null): CatalogueVariant | null {
  const { index } = useCatalogue();

  return useMemo(() => {
    if (id === null) return null;
    return index.entries.find((entry) => entry.variant.id === id)?.variant ?? null;
  }, [index, id]);
}

/** Exact barcode lookup, for the scanner listener. Stable, so it can sit in a dependency array. */
export function useBarcodeLookup(): (code: string) => CatalogueVariant | null {
  const { index } = useCatalogue();
  return useCallback((code: string) => index.byBarcode.get(code.trim()) ?? null, [index]);
}
